import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { pluralizeIssues } from '../../../../../shared/helpers/plural';
import { COUNT_MAX_FE, ENDPOINTS } from '../../../../../shared/issuesV2';
import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessageFromResponse } from '../../../../helpers/errors';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { get } from '../../../../redux/issuesV2/utils';
import type {
  AnyExportRequest,
  Category,
  Issue,
  IssuesResponse,
  JiraIssueExportRequest,
} from '../../../../../shared/issuesV2';
import type { APIError, FailedFetchResponse } from '../../../../helpers/errors';

import ExportIssuesModal from './ExportIssuesModal';

type ModalIssueExportBaseProps = {
  show: boolean;
  selectedIssuesCount: number;
  onHide: () => void;
};

type ModalIssueExportInboxProps = ModalIssueExportBaseProps & {
  issueCategory: Category;
};

/**
 * Container component for ExportIssuesModal, for use in the IssuesInbox
 */
export const ExportIssuesModalInboxContainer: VoidFunctionComponent<ModalIssueExportInboxProps> = ({
  show,
  onHide,
  selectedIssuesCount,
  issueCategory,
}) => {
  const dispatch = useDispatch();
  const addToast = useAddToast();

  const [content, setContent] = useState<AnyExportRequest>({
    type: 'jira',
    title: 'Loading...',
    description: 'Loading...',
  } satisfies JiraIssueExportRequest);

  const [fetchIssuesErrors, setFetchIssuesErrors] = useState<APIError & string>();

  const selectedIssues = useSelector(IssuesV2Store.selectors.getSelectedIssues);
  const selectedIssuesQuery = useSelector(IssuesV2Store.selectors.getSelectedIssuesFilterQuery);

  const [issues, setIssues] = useState<Issue[] | null>(null);

  const onEnter = async () => {
    setFetchIssuesErrors(undefined);

    // Fetch issue details if the frontend doesn't have the necessary data.
    // This can happen if either:
    // * all issues are selected across all pages
    // * no individual issues are selected (this means that only revisions are selected)
    const shouldFetchIssues = selectedIssues === 'all' || selectedIssues.length === 0;

    if (shouldFetchIssues) {
      try {
        const issuesResponse = await get<IssuesResponse<typeof issueCategory>>(ENDPOINTS.FETCH_ISSUES, {
          ...selectedIssuesQuery,
          count: COUNT_MAX_FE,
        });
        setIssues(issuesResponse.data?.issues ?? []);
      } catch (e) {
        setFetchIssuesErrors(errorMessageFromResponse(e as FailedFetchResponse));
      }
    } else {
      const selectedIssuesList = selectedIssues as Issue[];
      setIssues(selectedIssuesList);
    }
  };

  //  Handle what should happen when the user clicks the Export button
  const onExport = async () => {
    await dispatch(IssuesV2Store.actions.exportSelectedIssues(content));

    onHide();
    addToast({
      type: 'success',
      content: `Exported ${pluralizeIssues(selectedIssuesCount)}`,
    });
  };

  return (
    <ExportIssuesModal
      isShowing={show}
      onHide={onHide}
      onEnter={onEnter}
      selectedIssuesCount={selectedIssuesCount}
      issueCategory={issueCategory}
      issues={issues}
      setContent={setContent}
      onExport={onExport}
      fetchIssuesErrors={[fetchIssuesErrors] as string[]}
    />
  );
};

type ModalIssueExportDetailsProps = Omit<ModalIssueExportBaseProps, 'selectedIssuesCount'> & {
  id: number;
  issue: Issue;
  selectedIssuesCount?: number;
  //  For global issue details, there could be multiple existing Jira tickets,
  //  so we need to indicate to users that the existing tickets will be unlinked to create one new ticket.
  jiraTicketCount?: number;
  remediationVersion?: string;
};

/**
 * Container component for ExportIssuesModal, for use in the Issue Details
 */
export const ExportIssuesModalDetailsContainer: VoidFunctionComponent<ModalIssueExportDetailsProps> = ({
  id,
  issue,
  show,
  onHide,
  selectedIssuesCount = 1,
  jiraTicketCount = 0,
  remediationVersion,
}) => {
  const dispatch = useDispatch();
  const addToast = useAddToast();
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const [content, setContent] = useState<AnyExportRequest>({
    type: 'jira',
    title: 'Loading...',
    description: 'Loading...',
  });

  //  Handle what should happen when the user clicks the Export button
  const onExport = async () => {
    //  We need the action to hit the backend before we re-fetch the status summary.
    await dispatch(IssuesV2Store.actions.exportIssues({ issueIds: [id] }, content));

    //  This handles either closing the drawer (if on the inbox) or
    //  re-fetching the issue details (if we're on the full-page).
    dispatch(IssuesV2Store.actions.refetchIssueDetailsAfterAction(id));

    onHide();
    addToast({
      type: 'success',
      content: 'Exported issue.',
    });
  };

  return (
    <ExportIssuesModal
      isShowing={show}
      onHide={onHide}
      selectedIssuesCount={selectedIssuesCount}
      issueCategory={issueCategory}
      issues={[issue]}
      setContent={setContent}
      onExport={onExport}
      jiraTicketCount={jiraTicketCount}
      remediationVersion={remediationVersion}
    />
  );
};
